import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle, Loader } from "lucide-react";
import { ProTrackMark } from "../components/ProTrackLogo";
import { publicSubmissionsAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";

const FIELD_STYLE = {
  width: "100%",
  padding: "12px 14px",
  background: "rgba(255,255,255,0.03)",
  border: "1px solid rgba(232,234,240,0.12)",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "14px",
  fontFamily: "'DM Sans', sans-serif",
  outline: "none",
};

const LABEL_STYLE = { display: "block", fontSize: "12px", fontWeight: "700", color: "rgba(232,234,240,0.6)", marginBottom: "6px" };

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: "20px" }}>
      <label style={LABEL_STYLE}>{label}{required && <span style={{ color: "#ff8c42" }}> *</span>}</label>
      {children}
    </div>
  );
}

const EMPTY_FORM = {
  submitter_name: "", submitter_organization: "", submitter_email: "", submitter_phone: "",
  title: "", agency: "", description: "", category: "", location: "",
  contract_value: "", submission_deadline: "", requirements: "", eligibility: "",
  contact_email: "", contact_phone: "",
};

export default function SubmitTender() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        contract_value: form.contract_value ? Number(form.contract_value) : null,
        submission_deadline: form.submission_deadline || null,
        submitter_phone: form.submitter_phone || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        description: form.description || null,
        category: form.category || null,
        location: form.location || null,
        requirements: form.requirements || null,
        eligibility: form.eligibility || null,
      };
      await publicSubmissionsAPI.submit(payload);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong submitting your tender. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#080d1a", color: "#e8eaf0", fontFamily: "'DM Sans', sans-serif" }}>
      <div style={{ maxWidth: "640px", margin: "0 auto", padding: "56px 24px 100px" }}>
        {isAuthenticated && (
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="btn btn-ghost"
            style={{ marginBottom: "20px", padding: "8px 16px", fontSize: "13px" }}
          >
            <ArrowLeft size={15} />
            Back to Dashboard
          </button>
        )}
        <div
          style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "40px", cursor: "pointer", width: "fit-content" }}
          onClick={() => navigate(isAuthenticated ? "/dashboard" : "/")}
        >
          <ProTrackMark size={30} />
          <span style={{ fontSize: "18px", fontWeight: "800", color: "#fff" }}>
            ProTrack<span style={{ color: "#00d4a1" }}>NG</span>
          </span>
        </div>

        {done ? (
          <div className="card" style={{ textAlign: "center", padding: "56px 32px" }}>
            <CheckCircle size={48} color="#00d4a1" style={{ marginBottom: "16px" }} />
            <h2 style={{ fontSize: "22px", fontWeight: "800", color: "#fff", marginBottom: "8px" }}>Tender submitted for review</h2>
            <p style={{ fontSize: "14px", color: "rgba(232,234,240,0.6)", lineHeight: "1.7", marginBottom: "24px" }}>
              Thank you. Our team reviews every submission before it's published to bidders — you'll get an email at{" "}
              <strong style={{ color: "#fff" }}>{form.submitter_email}</strong> as soon as a decision is made, usually within 2 business days.
            </p>
            <button onClick={() => { setForm(EMPTY_FORM); setDone(false); }} className="btn btn-primary" style={{ padding: "10px 24px", fontSize: "13px" }}>
              Submit another tender
            </button>
          </div>
        ) : (
          <>
            <h1 style={{ fontSize: "clamp(26px, 5vw, 36px)", fontWeight: "900", letterSpacing: "-1px", fontFamily: "'DM Serif Display', Georgia, serif", marginBottom: "8px" }}>
              Submit a Tender
            </h1>
            <p style={{ fontSize: "14px", color: "rgba(232,234,240,0.55)", lineHeight: "1.7", marginBottom: "36px" }}>
              For government agencies, parastatals, and companies to publish tenders directly to Nigerian bidders on ProTrackNG.
              Every submission is reviewed by our team before it goes live — it won't appear anywhere until approved.
            </p>

            <form onSubmit={handleSubmit}>
              <h3 style={{ fontSize: "13px", fontWeight: "800", color: "#00d4a1", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "16px" }}>Your details</h3>
              <Field label="Your name" required>
                <input style={FIELD_STYLE} required value={form.submitter_name} onChange={set("submitter_name")} placeholder="e.g. Amaka Okafor" />
              </Field>
              <Field label="Organization" required>
                <input style={FIELD_STYLE} required value={form.submitter_organization} onChange={set("submitter_organization")} placeholder="e.g. Rivers State Ministry of Works" />
              </Field>
              <Field label="Your email" required>
                <input type="email" style={FIELD_STYLE} required value={form.submitter_email} onChange={set("submitter_email")} placeholder="you@youragency.gov.ng" />
              </Field>
              <Field label="Your phone">
                <input style={FIELD_STYLE} value={form.submitter_phone} onChange={set("submitter_phone")} placeholder="+234..." />
              </Field>

              <h3 style={{ fontSize: "13px", fontWeight: "800", color: "#00d4a1", textTransform: "uppercase", letterSpacing: "0.5px", margin: "28px 0 16px" }}>Tender details</h3>
              <Field label="Tender title" required>
                <input style={FIELD_STYLE} required value={form.title} onChange={set("title")} placeholder="e.g. Supply and Installation of Solar Streetlights — Phase 2" />
              </Field>
              <Field label="Issuing agency" required>
                <input style={FIELD_STYLE} required value={form.agency} onChange={set("agency")} placeholder="e.g. Rivers State Ministry of Works" />
              </Field>
              <Field label="Description">
                <textarea style={{ ...FIELD_STYLE, minHeight: "100px", resize: "vertical" }} value={form.description} onChange={set("description")} placeholder="What the contract covers, scope of work, etc." />
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <Field label="Category">
                  <input style={FIELD_STYLE} value={form.category} onChange={set("category")} placeholder="e.g. Construction, IT, Supplies" />
                </Field>
                <Field label="Location">
                  <input style={FIELD_STYLE} value={form.location} onChange={set("location")} placeholder="e.g. Port Harcourt, Rivers State" />
                </Field>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <Field label="Estimated contract value (₦)">
                  <input type="number" style={FIELD_STYLE} value={form.contract_value} onChange={set("contract_value")} placeholder="45000000" />
                </Field>
                <Field label="Submission deadline">
                  <input type="date" style={FIELD_STYLE} value={form.submission_deadline} onChange={set("submission_deadline")} />
                </Field>
              </div>
              <Field label="Requirements">
                <textarea style={{ ...FIELD_STYLE, minHeight: "80px", resize: "vertical" }} value={form.requirements} onChange={set("requirements")} placeholder="Required documents, certifications, technical specs" />
              </Field>
              <Field label="Eligibility criteria">
                <textarea style={{ ...FIELD_STYLE, minHeight: "80px", resize: "vertical" }} value={form.eligibility} onChange={set("eligibility")} placeholder="Who can bid — company size, sector experience, etc." />
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <Field label="Bid inquiries — contact email">
                  <input type="email" style={FIELD_STYLE} value={form.contact_email} onChange={set("contact_email")} placeholder="procurement@youragency.gov.ng" />
                </Field>
                <Field label="Bid inquiries — contact phone">
                  <input style={FIELD_STYLE} value={form.contact_phone} onChange={set("contact_phone")} placeholder="+234..." />
                </Field>
              </div>

              {error && (
                <div style={{ background: "rgba(255,77,109,0.1)", border: "1px solid rgba(255,77,109,0.2)", borderRadius: "8px", padding: "12px 16px", fontSize: "13px", color: "#ff4d6d", marginBottom: "20px" }}>
                  {error}
                </div>
              )}

              <button type="submit" disabled={submitting} className="btn btn-primary" style={{ width: "100%", padding: "14px", fontSize: "14px", fontWeight: "700", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                {submitting ? (<><Loader size={16} className="spin" /> Submitting…</>) : "Submit for review"}
              </button>
              <p style={{ fontSize: "12px", color: "rgba(232,234,240,0.4)", textAlign: "center", marginTop: "12px" }}>
                Submissions are reviewed before publishing — this is not an automatic listing.
              </p>
              <p style={{ fontSize: "12px", color: "rgba(232,234,240,0.4)", textAlign: "center", marginTop: "8px" }}>
                By submitting, you confirm the tender is genuine and you're authorized to publish it — see our{" "}
                <a href="#/terms" style={{ color: "#00d4a1", textDecoration: "none" }}>Terms</a>
                {" "}and{" "}
                <a href="#/privacy" style={{ color: "#00d4a1", textDecoration: "none" }}>Privacy Policy</a>.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
